
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, signout } from "../features/auth/authSlice";
import { Link, useNavigate } from "react-router-dom";
import { setSubject } from "../features/subject/subjectSlice";
import { ColorModeContext } from "../../src/ThemeContext";
import { useEffect } from "react";
import { validateSession, setupSessionCheck, isTokenExpired } from "../utils/sessionUtils";
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";

import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
  Container,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  DialogContent,
  Dialog,
} from "@mui/material";
import { useContext, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { NavLink } from "react-router-dom";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import codingComitfavlogo from '../assets/codingcoachlogo-favicon.png'
import SignIn from "../pages/SignIn";
import SignUp from "../pages/SignUp";


const Navbar = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);

  const [openSignIn, setOpenSignIn] = useState(false);
  const [openSignUp, setOpenSignUp] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  // ✅ signout handler
  const handleSignOut = () => {
    dispatch(logoutUser());
    navigate("/");
  };

  // ✅ Session validation is now centralized in App.js to avoid redundant calls
  // and race conditions, especially during Google OAuth login flow.


  // ✅ navigation items
  const menuItems = [
    { label: "Home", to: "/" },
    { label: "Python", to: "/python", subject: "python" },
    { label: "FastAPI", to: "/fastapi", subject: "fastapi" },
    { label: "DSA", to: "/dsa", subject: "dsa" },
    { label: "Agentic AI", to: "/agentic-ai", subject: "agentic-ai" },
    { label: "HTML", to: "/html", subject: "html" },
    { label: "CSS", to: "/css", subject: "css" },
    { label: "JavaScript", to: "/javascript", subject: "javascript" },
    { label: "SQL", to: "/sql", subject: "sql" },
    { label: "Practice", to: "/practice" },
    // { label: "React", to: "/react" },
    ...(user?.role === "admin"
      ? [{ label: "Admin", to: "/admin" }]
      : []),
  ];

  // ✅ auth section
  const displayName = user?.username || user?.name || user?.email || "User";
  const authItems = !user
    ? [
      { label: "Sign Up", onClick: () => setOpenSignUp(true), icon: <PersonAddIcon /> },
      { label: "Sign In", onClick: () => setOpenSignIn(true), icon: <LoginIcon /> },
    ]
    : [
      {
        label: `Hello, ${displayName}`,
        picture: user?.picture,
        isText: true,
      },
      { label: "Sign Out", onClick: handleSignOut, icon: <LogoutIcon /> },
    ];

  // Colors matching home page
  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
  };

  return (
    <>
      {/* 🌙 AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: isDark ? "rgba(10, 10, 10, 0.8)" : "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(12px)",
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          transition: "all 0.4s ease",
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
            {/* Logo / Brand */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                gap: 1.5,
              }}
            >
              <img
                src={codingComitfavlogo}
                alt="CodingComet"
                style={{ width: 40, height: 40 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    color: "#d50000",
                    fontSize: 22,
                    letterSpacing: 0.5,
                    lineHeight: 1.2,
                  }}
                >
                  CodingComet
                </Typography>
              </Box>
            </Box>

            {/* Navigation + Auth */}
            {isMobile ? (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="Toggle navigation menu"
                onClick={() => setMobileOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <>
                {/* Main Menu */}
                <Stack direction="row" spacing={2} alignItems="center">
                  {menuItems.map((item) => (
                    <Button
                      key={item.to}
                      component={NavLink}
                      to={item.to}
                      sx={{
                        textTransform: "capitalize",
                        color: theme.palette.text.primary,
                        "&:hover": { color: theme.palette.primary.main },
                        "&.active": {
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                        },
                      }}
                      onClick={() => {
                        if (item.subject) dispatch(setSubject(item.subject));
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>

                {/* Auth + Theme Toggle */}
                <Stack direction="row" spacing={2} alignItems="center">
                  {authItems.map((item, index) =>
                    item.isText ? (
                      <Stack key={index} direction="row" alignItems="center" spacing={1}>
                        {item.picture ? (
                          <Avatar
                            src={item.picture}
                            alt={displayName}
                            sx={{
                              width: 32,
                              height: 32,
                              border: `2px solid ${theme.palette.primary.main}`,
                            }}
                          />
                        ) : (
                          <PersonIcon sx={{ color: theme.palette.primary.main }} />
                        )}
                        <Typography sx={{ textTransform: "capitalize" }}>
                          {item.label}
                        </Typography>
                      </Stack>
                    ) : (
                      <Button
                        key={index}
                        variant={item.label === "Sign Up" ? "contained" : "text"}
                        sx={{
                          color: item.label === "Sign Up" ? "#fff" : theme.palette.text.primary,
                          background: item.label === "Sign Up" ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` : "transparent",
                          fontWeight: 700,
                          borderRadius: "8px",
                          px: item.label === "Sign Up" ? 3 : 2,
                          "&:hover": {
                            color: item.label === "Sign Up" ? "#fff" : theme.palette.primary.main,
                            background: item.label === "Sign Up" ? `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})` : "transparent"
                          },
                        }}
                        onClick={item.onClick}
                        startIcon={item.icon || null}
                      >
                        {item.label}
                      </Button>
                    )
                  )}

                  {/* 🌗 Theme Toggle */}
                  <Tooltip title="Toggle light/dark mode">
                    <IconButton
                      onClick={colorMode.toggleColorMode}
                      color="inherit"
                      sx={{
                        ml: 1,
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "rotate(20deg)" },
                      }}
                    >
                      {isDark ? <Brightness7 /> : <Brightness4 />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar >

      {/* 📱 Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: false,
        }}
        PaperProps={{
          sx: {
            width: 250,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <Box sx={{ width: 250, p: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  onClick={() => {
                    if (item.subject) dispatch(setSubject(item.subject));
                    setMobileOpen(false);
                  }}
                  sx={{
                    "&.active": {
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                    },
                  }}
                >
                  <ListItemText primary={item.label} sx={{ ml: 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <List sx={{ mt: 2 }}>
            {authItems.map((item, index) => (
              <ListItem key={index} disablePadding>
                {item.isText ? (
                  <ListItemButton>
                    {item.picture ? (
                      <Avatar
                        src={item.picture}
                        alt={displayName}
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          border: `1px solid ${theme.palette.primary.main}`,
                        }}
                      />
                    ) : (
                      <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    )}
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    onClick={() => {
                      if (item.onClick) item.onClick();
                      setMobileOpen(false);
                    }}
                  >
                    {item.icon && item.icon}
                    <ListItemText primary={item.label} sx={{ ml: 1 }} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}

            {/* 🌙 Theme toggle in drawer */}
            <ListItem disablePadding>
              <ListItemButton onClick={colorMode.toggleColorMode}>
                <ListItemText
                  primary={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                />
                {isDark ? <Brightness7 /> : <Brightness4 />}
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer >

      {/* 🔐 SignIn/SignUp Dialogs */}
      < Dialog open={openSignIn} onClose={() => setOpenSignIn(false)} maxWidth="sm" fullWidth >
        <DialogContent>
          <SignIn onClose={() => setOpenSignIn(false)} />
        </DialogContent>
      </Dialog >

      <Dialog open={openSignUp} onClose={() => setOpenSignUp(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <SignUp
            onClose={() => setOpenSignUp(false)}
            onSwitchToSignIn={() => {
              setOpenSignUp(false);
              setOpenSignIn(true);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
